import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Briefcase, Users, Shield, Clock, DollarSign, MapPin, MessageSquare, 
  Star, CheckCircle, ArrowRight, Menu, X, ChevronRight, Zap, Building2, 
  UserCheck, Award, Globe, Activity, Stethoscope, Heart, Calendar
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === 'worker') navigate('/worker');
      else if (user.role === 'employer') navigate('/employer');
      else if (user.role === 'agent') navigate('/agent');
      else if (user.role === 'admin') navigate('/admin');
    }
  }, [user, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'GPS-Based Matching',
      description: 'Smart location-based job discovery with geofencing for accurate attendance tracking'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Verified Professionals',
      description: 'All workers undergo background checks and credential verification'
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Secure Escrow Payments',
      description: 'Protected payment system with automatic release upon job completion'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Real-Time Updates',
      description: 'Instant notifications for shifts, applications, and schedule changes'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'In-App Messaging',
      description: 'Direct communication between employers and healthcare professionals'
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: 'Rating System',
      description: 'Two-way reviews to maintain quality and trust across the platform'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Active Workers', icon: <Users className="w-5 h-5" /> },
    { number: '500+', label: 'Employers', icon: <Building2 className="w-5 h-5" /> },
    { number: '50K+', label: 'Jobs Completed', icon: <CheckCircle className="w-5 h-5" /> },
    { number: '99%', label: 'Satisfaction Rate', icon: <Award className="w-5 h-5" /> }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Industrial Worker',
      rating: 5,
      text: 'ScensobConnect has transformed how I find jobs. The GPS check-in makes everything seamless, and payments are always on time.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Michael Chen',
      role: 'Business Owner',
      rating: 5,
      text: 'We filled 200+ positions last month with verified professionals. The platform saved us countless hours in staffing coordination.',
      image: 'https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Lisa Rodriguez',
      role: 'Staffing Agency Director',
      rating: 5,
      text: 'Managing our worker pool has never been easier. The dual functionality is exactly what agencies need.',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const industries = [
    { name: 'Healthcare', icon: <Stethoscope className="w-5 h-5" />, count: '2.4K jobs' },
    { name: 'Hospitality', icon: <Building2 className="w-5 h-5" />, count: '1.8K jobs' },
    { name: 'Industrial', icon: <Activity className="w-5 h-5" />, count: '950 jobs' },
    { name: 'Retail', icon: <Calendar className="w-5 h-5" />, count: '1.2K jobs' }
  ];

  const portals = [
    {
      title: 'Worker Portal',
      description: 'Find jobs across multiple industries, manage your schedule, and get paid instantly.',
      icon: <Users className="w-8 h-8" />,
      features: ['Browse jobs by industry', 'GPS-based check-in', 'Instant payments', 'Build your profile'],
      cta: 'Enter Worker Portal',
      href: 'https://scenso-staffing-1.preview.emergentagent.com/worker',
      external: true,
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      title: 'Employer Portal',
      description: 'Post jobs, find verified workers, and manage your workforce efficiently.',
      icon: <Building2 className="w-8 h-8" />,
      features: ['Post unlimited jobs', 'Verified worker pool', 'Escrow protection', 'Analytics dashboard'],
      cta: 'Enter Employer Portal',
      href: '/login',
      gradient: 'from-violet-500 to-purple-500'
    },
    {
      title: 'Agency Portal',
      description: 'Manage your staffing agency with tools for both workers and employers.',
      icon: <UserCheck className="w-8 h-8" />,
      features: ['Worker pool management', 'Client relationships', 'Bulk operations', 'Commission tracking'],
      cta: 'Enter Agency Portal',
      href: '/login',
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Admin Portal',
      description: 'Full platform oversight — manage users, monitor activity, and configure settings.',
      icon: <Shield className="w-8 h-8" />,
      features: ['User management', 'Platform analytics', 'System configuration', 'Audit logs'],
      cta: 'Enter Admin Portal',
      href: '/login',
      gradient: 'from-rose-500 to-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative grain">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass border-b border-slate-800/50' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-cyan-400 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-slate-950" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight font-['Manrope']">
                Scensob<span className="text-cyan-400">Connect</span>
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-400 hover:text-cyan-400 transition-colors">
                Features
              </a>
              <a href="#portals" className="text-sm font-medium text-slate-400 hover:text-cyan-400 transition-colors">
                Portals
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-400 hover:text-cyan-400 transition-colors">
                How It Works
              </a>
              <a href="#testimonials" className="text-sm font-medium text-slate-400 hover:text-cyan-400 transition-colors">
                Testimonials
              </a>
              <a href="#pricing" className="text-sm font-medium text-slate-400 hover:text-cyan-400 transition-colors">
                Pricing
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                data-testid="nav-sign-in"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2.5 text-sm font-semibold text-slate-950 bg-cyan-400 rounded-full hover:bg-cyan-300 btn-glow transition-all"
                data-testid="nav-get-started"
              >
                Get Started Free
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-slate-800 glass">
              <div className="flex flex-col gap-1">
                <a href="#features" className="px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg">Features</a>
                <a href="#portals" className="px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg">Portals</a>
                <a href="#how-it-works" className="px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg">How It Works</a>
                <a href="#testimonials" className="px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg">Testimonials</a>
                <a href="#pricing" className="px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg">Pricing</a>
                <div className="pt-4 mt-2 border-t border-slate-800 flex flex-col gap-2 px-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full py-3 text-sm font-medium text-slate-300 border border-slate-700 rounded-full hover:bg-slate-800"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 text-sm font-semibold text-slate-950 bg-cyan-400 rounded-full hover:bg-cyan-300"
                  >
                    Get Started Free
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-32 overflow-hidden hero-glow">
        {/* Background Image */}
        <div className="absolute inset-0 -z-10">
          <img 
            src="https://images.unsplash.com/photo-1737998245935-8bc0eefebd92?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Healthcare background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left animate-fadeUp">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full mb-6">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-cyan-400">On-Demand Staffing Reimagined</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1] tracking-tight mb-6 font-['Manrope']">
                Connect with
                <span className="text-gradient-cyan"> Verified Workers</span>
                {' '}On-Demand
              </h1>
              
              <p className="text-lg lg:text-xl text-slate-400 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                The premier real-time employment marketplace for all industries. Find workers or jobs instantly across healthcare, hospitality, industrial, and more.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <button
                  onClick={() => navigate('/register')}
                  className="group px-8 py-4 text-base font-semibold text-slate-950 bg-cyan-400 rounded-full hover:bg-cyan-300 btn-glow transition-all flex items-center justify-center gap-2"
                  data-testid="hero-get-started"
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 text-base font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-full hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="hero-sign-in"
                >
                  Sign In to Dashboard
                </button>
              </div>

              {/* Quick Industries */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                {industries.map((ind) => (
                  <div key={ind.name} className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 border border-slate-800 rounded-full text-sm">
                    <span className="text-cyan-400">{ind.icon}</span>
                    <span className="text-slate-300">{ind.name}</span>
                    <span className="text-slate-500">•</span>
                    <span className="text-slate-500">{ind.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Stats Card */}
            <div className="relative animate-fadeUp" style={{ animationDelay: '0.2s' }}>
              {/* Main Card */}
              <div className="relative glass rounded-3xl overflow-hidden border border-slate-800">
                <img 
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Team collaboration" 
                  className="w-full h-64 lg:h-72 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
                
                {/* Stats Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, idx) => (
                      <div 
                        key={stat.label || idx} 
                        className="p-4 bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-800 hover:border-cyan-500/30 transition-colors group"
                        data-testid={`stat-${idx}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1.5 bg-cyan-400/10 rounded-lg text-cyan-400 group-hover:bg-cyan-400 group-hover:text-slate-950 transition-colors">
                            {stat.icon}
                          </div>
                        </div>
                        <div className="text-2xl lg:text-3xl font-bold text-white tabular-nums">{stat.number}</div>
                        <div className="text-sm text-slate-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 lg:top-8 lg:-right-8 bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-4 animate-pulse-glow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <Activity className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Live Now</div>
                    <div className="text-xs text-slate-500">2.4k workers online</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Platform Features</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Manrope']">
              Enterprise-Grade Features
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Everything you need to manage staffing efficiently and securely
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div
                key={feature.title || idx}
                className="group relative p-8 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-800 card-hover"
                data-testid={`feature-card-${idx}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mb-6 text-cyan-400 group-hover:bg-cyan-400 group-hover:text-slate-950 transition-colors">
                  {feature.icon}
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-3 font-['Manrope']">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover Arrow */}
                <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-cyan-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portals Section */}
      <section id="portals" className="py-20 lg:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider">Choose Your Portal</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Manrope']">
              Tailored for Everyone
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Whether you're a worker, employer, or staffing agency - we have the perfect portal for you
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {portals.map((portal, idx) => (
              <div
                key={portal.title || portal.name || idx}
                className="group relative p-8 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-800 card-hover overflow-hidden"
                data-testid={`portal-card-${idx}`}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${portal.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />
                
                {/* Icon */}
                <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${portal.gradient} flex items-center justify-center mb-6 text-white shadow-lg`}>
                  {portal.icon}
                </div>
                
                <h3 className="relative text-2xl font-bold text-white mb-3 font-['Manrope']">
                  {portal.title}
                </h3>
                <p className="relative text-slate-400 leading-relaxed mb-6">
                  {portal.description}
                </p>
                
                {/* Features List */}
                <ul className="relative space-y-3 mb-8">
                  {portal.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA Button */}
                <button
                  onClick={() => portal.external ? window.location.href = portal.href : navigate(portal.href)}
                  className={`relative w-full py-3 rounded-full font-semibold transition-all flex items-center justify-center gap-2 bg-gradient-to-r ${portal.gradient} text-white hover:shadow-lg hover:shadow-${portal.gradient.split('-')[1]}-500/20 hover:scale-[1.02] active:scale-100`}
                  data-testid={`portal-cta-${idx}`}
                >
                  {portal.cta}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Simple Process</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Manrope']">
              How It Works
            </h2>
            <p className="text-lg text-slate-400">
              Get started in minutes, not days
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { 
                step: '01', 
                title: 'Create Account', 
                desc: 'Sign up as a worker, employer, or staffing agent in under 2 minutes',
                icon: <UserCheck className="w-6 h-6" />
              },
              { 
                step: '02', 
                title: 'Post or Find Jobs', 
                desc: 'Employers post jobs, workers discover opportunities by industry near them',
                icon: <Briefcase className="w-6 h-6" />
              },
              { 
                step: '03', 
                title: 'Work & Get Paid', 
                desc: 'GPS check-in, complete the job, and receive secure instant payment',
                icon: <DollarSign className="w-6 h-6" />
              }
            ].map((item, idx) => (
              <div key={item.title || idx} className="relative text-center group" data-testid={`step-${idx}`}>
                {/* Connector Line */}
                {idx < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-full h-0.5 bg-gradient-to-r from-cyan-500/30 to-transparent" />
                )}
                
                {/* Step Number */}
                <div className="relative inline-flex mb-6">
                  <div className="w-24 h-24 rounded-3xl bg-slate-900 border-2 border-slate-800 flex items-center justify-center group-hover:border-cyan-500/50 group-hover:shadow-[0_0_30px_rgba(34,211,238,0.15)] transition-all">
                    <span className="text-3xl font-bold text-cyan-400 font-['Manrope']">{item.step}</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-cyan-400 rounded-xl flex items-center justify-center text-slate-950">
                    {item.icon}
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-3 font-['Manrope']">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Testimonials</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Manrope']">
              Trusted by Healthcare Leaders
            </h2>
            <p className="text-lg text-slate-400">
              See what our community has to say
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx} 
                className="group p-8 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-800 card-hover"
                data-testid={`testimonial-${idx}`}
              >
                {/* Rating */}
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-slate-300 leading-relaxed mb-6">"{testimonial.text}"</p>
                
                {/* Author */}
                <div className="flex items-center gap-4 pt-6 border-t border-slate-800">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-800"
                  />
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider">Pricing</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Manrope']">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-400">
              Only pay when you work or hire
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Workers */}
            <div className="p-8 rounded-2xl bg-slate-900/50 border border-slate-800 card-hover" data-testid="pricing-workers">
              <div className="mb-8">
                <div className="text-lg font-semibold text-white mb-2 font-['Manrope']">Workers</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-white">5%</span>
                  <span className="text-slate-500">/ job</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">Service fee per completed job</p>
              </div>
              <ul className="space-y-4 mb-8">
                {['Browse jobs by industry', 'Instant secure payments', 'GPS attendance tracking', 'In-app messaging', 'Build your profile'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/register')}
                className="w-full py-3 text-sm font-semibold text-slate-300 bg-slate-800 border border-slate-700 rounded-full hover:bg-slate-700 transition-colors"
              >
                Start as Worker
              </button>
            </div>

            {/* Employers - Featured */}
            <div className="relative p-8 rounded-2xl bg-slate-900 border-2 border-cyan-500/50 glow-cyan scale-105" data-testid="pricing-employers">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-cyan-400 text-slate-950 text-xs font-bold rounded-full">
                MOST POPULAR
              </div>
              <div className="mb-8">
                <div className="text-lg font-semibold text-white mb-2 font-['Manrope']">Employers</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-white">3%</span>
                  <span className="text-slate-400">/ hire</span>
                </div>
                <p className="text-sm text-slate-400 mt-2">Service fee per successful placement</p>
              </div>
              <ul className="space-y-4 mb-8">
                {['Unlimited job postings', 'Verified worker pool', 'Escrow protection', 'Priority support', 'Analytics dashboard', 'Multi-industry support'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-200">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/register')}
                className="w-full py-3 text-sm font-semibold text-slate-950 bg-cyan-400 rounded-full hover:bg-cyan-300 btn-glow transition-all"
              >
                Start as Employer
              </button>
            </div>

            {/* Agents */}
            <div className="p-8 rounded-2xl bg-slate-900/50 border border-slate-800 card-hover" data-testid="pricing-agencies">
              <div className="mb-8">
                <div className="text-lg font-semibold text-white mb-2 font-['Manrope']">Staffing Agents</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-white">Custom</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">Volume pricing available</p>
              </div>
              <ul className="space-y-4 mb-8">
                {['Worker pool management', 'Bulk job operations', 'Dedicated account manager', 'Custom integrations', 'White-label options', 'API access'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/register')}
                className="w-full py-3 text-sm font-semibold text-slate-300 bg-slate-800 border border-slate-700 rounded-full hover:bg-slate-700 transition-colors"
              >
                Start as Agent
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 relative overflow-hidden">
        {/* Background Glow */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full mb-8">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-cyan-400">Join 10,000+ professionals</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-['Manrope']">
            Ready to Transform Your Staffing?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of workers, employers, and agents already using ScensobConnect to revolutionize their staffing operations.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="group px-8 py-4 text-base font-semibold text-slate-950 bg-cyan-400 rounded-full hover:bg-cyan-300 btn-glow transition-all flex items-center justify-center gap-2"
              data-testid="cta-get-started"
            >
              Start Your Free Account
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 text-base font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-full hover:bg-slate-700 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-400 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-slate-950" />
                </div>
                <span className="text-xl font-bold text-white font-['Manrope']">
                  Scensob<span className="text-cyan-400">Connect</span>
                </span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
                The premier platform for on-demand staffing solutions. Connecting verified workers with opportunities across all industries.
              </p>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4 font-['Manrope']">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="text-slate-500 hover:text-cyan-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-slate-500 hover:text-cyan-400 transition-colors">Pricing</a></li>
                <li><Link to="/security" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-security">Security</Link></li>
                <li><Link to="/enterprise" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-enterprise">Enterprise</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 font-['Manrope']">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-about-us">About Us</Link></li>
                <li><Link to="/careers" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-careers">Careers</Link></li>
                <li><Link to="/blog" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-blog">Blog</Link></li>
                <li><Link to="/contact" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-contact">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4 font-['Manrope']">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-terms-of-service">Terms of Service</Link></li>
                <li><Link to="/hipaa" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-hipaa-notice">HIPAA Compliance</Link></li>
                <li><Link to="/cookies" className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="footer-link-cookies">Cookies</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-600">&copy; {new Date().getFullYear()} ScensobConnect. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a
                href="https://twitter.com/scensobconnect"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ScensobConnect on Twitter"
                className="text-slate-600 hover:text-cyan-400 transition-colors"
                data-testid="footer-social-twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a
                href="https://www.linkedin.com/company/scensobconnect"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ScensobConnect on LinkedIn"
                className="text-slate-600 hover:text-cyan-400 transition-colors"
                data-testid="footer-social-linkedin"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
